import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { ArrowLeft, Check, BadgeCheck, Package, Ship } from "lucide-react";
import { MaskLines, Reveal } from "../lib/motion";
import { BrandInline } from "./Brand";
import Contact from "./Contact";
import Footer from "./Footer";
import {
  BRANDS,
  QUALITY_CHECKS,
  PACKAGING_OPTIONS,
  EXPORT_CAPABILITIES,
  COMPLIANCE,
} from "../data";

export default function BrandPage() {
  const { key } = useParams();
  const brand = BRANDS.find((b) => b.key === key);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, [key]);

  if (!brand) return <Navigate to="/" replace />;
  const accent = brand.accent;

  return (
    <div className="bg-[#F8F7F3]" data-testid={`brand-page-${brand.key}`}>
      {/* Header */}
      <header className="absolute top-0 inset-x-0 z-50">
        <div className="max-w-[1400px] mx-auto h-[88px] px-6 md:px-10 flex items-center justify-between">
          <Link to="/"><BrandInline color="#F8F7F3" emblem="w-10 h-10" glow /></Link>
          <Link to="/" className="group inline-flex items-center gap-2 text-[#F8F7F3] font-body text-sm link-underline">
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[86svh] flex items-center overflow-hidden bg-[#111]">
        <div className="absolute inset-0">
          <img src={brand.image} alt={brand.name} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${accent}26, rgba(17,17,17,0.9))` }} />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-24">
          <motion.p className="overline mb-6" style={{ color: accent }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
            {brand.name} · {brand.tagline}
          </motion.p>
          <h1 className="hero-title font-display text-[#F8F7F3] max-w-[860px]">
            <MaskLines lines={[brand.heroTitle]} active delay={0.3} />
          </h1>
          <motion.p className="font-body text-[#EDE8DF]/80 text-lg md:text-xl max-w-[620px] mt-8 leading-relaxed"
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.9 }}>
            {brand.description}
          </motion.p>
          <motion.button
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="brand-quote-btn"
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9 }}
            className="mt-10 rounded-full font-head font-semibold px-9 py-4 text-white transition-transform duration-300 hover:scale-[1.03]"
            style={{ background: accent }}
          >
            Request a Quote
          </motion.button>
        </div>
      </section>

      {/* Story */}
      <section className="bg-[#F8F7F3] py-24 md:py-32">
        <div className="max-w-[820px] mx-auto px-6 text-center">
          <Reveal><p className="overline mb-6" style={{ color: accent }}>The Brand</p></Reveal>
          <Reveal delay={0.1}>
            <p className="font-display text-2xl md:text-3xl text-[#111] leading-snug">{brand.story}</p>
          </Reveal>
        </div>
      </section>

      {/* Product categories */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal><p className="overline mb-6" style={{ color: accent }}>Portfolio</p></Reveal>
          <Reveal delay={0.1}><h2 className="section-title font-display text-[#111] mb-14">Product Categories</h2></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brand.categories.map((cat, i) => (
              <Reveal key={cat.group} delay={(i % 3) * 0.08}>
                <div className="border border-[#E7E1D6] bg-[#F8F7F3] p-8 h-full">
                  <h3 className="font-head font-bold text-lg text-[#111] mb-5 pb-4 border-b" style={{ borderColor: accent + "55" }}>
                    {cat.group}
                  </h3>
                  <ul className="space-y-3">
                    {cat.items.map((it) => (
                      <li key={it} className="flex items-center gap-3 font-body text-[15px] text-[#444]">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Grade comparison table (Agraga) */}
          {brand.grades && (
            <div className="mt-16">
              <Reveal><h3 className="font-display text-2xl text-[#111] mb-6">Grade Comparison</h3></Reveal>
              <Reveal delay={0.1}>
                <div className="overflow-x-auto border border-[#E7E1D6]">
                  <table className="w-full text-left border-collapse min-w-[640px]" data-testid="grade-table">
                    <thead>
                      <tr className="bg-[#111] text-white">
                        {["Grade", "Size", "Colour", "Moisture", "Ideal Use"].map((h) => (
                          <th key={h} className="font-head font-semibold text-sm px-6 py-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {brand.grades.map((g, idx) => (
                        <tr key={g.grade} className={idx % 2 ? "bg-[#F8F7F3]" : "bg-white"}>
                          <td className="px-6 py-4 font-head font-semibold text-[#111]" style={{ color: accent }}>{g.grade}</td>
                          <td className="px-6 py-4 font-body text-sm text-[#444]">{g.size}</td>
                          <td className="px-6 py-4 font-body text-sm text-[#444]">{g.color}</td>
                          <td className="px-6 py-4 font-body text-sm text-[#444]">{g.moisture}</td>
                          <td className="px-6 py-4 font-body text-sm text-[#444]">{g.use}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="font-body text-sm text-[#666] mt-4">
                  Custom specifications available on request — share your requirement in the quote form below.
                </p>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      {/* Quality / Packaging / Export */}
      <section className="bg-[#111] py-24 md:py-32 grain relative overflow-hidden">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-3 gap-12">
          <Reveal>
            <p className="overline mb-6" style={{ color: accent }}><Check size={16} className="inline mr-2" />Quality</p>
            <ul className="space-y-4">
              {QUALITY_CHECKS.map((c) => (
                <li key={c} className="flex items-center gap-3 border-b border-white/10 pb-3 font-body text-[15px] text-white/85">
                  <BadgeCheck size={16} style={{ color: accent }} /> {c}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline mb-6" style={{ color: accent }}><Package size={16} className="inline mr-2" />Packaging</p>
            <ul className="space-y-4">
              {PACKAGING_OPTIONS.map((p) => (
                <li key={p} className="flex items-center gap-3 border-b border-white/10 pb-3 font-body text-[15px] text-white/85">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="overline mb-6" style={{ color: accent }}><Ship size={16} className="inline mr-2" />Export</p>
            <ul className="space-y-4">
              {EXPORT_CAPABILITIES.map((p) => (
                <li key={p} className="flex items-center gap-3 border-b border-white/10 pb-3 font-body text-[15px] text-white/85">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Certifications */}
      <section className="bg-[#F8F7F3] py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal><p className="overline text-[#B08D57] mb-8">International Certifications</p></Reveal>
          <div className="flex flex-wrap gap-3">
            {COMPLIANCE.map((c) => (
              <span key={c.code} className="rounded-full border border-[#E7E1D6] bg-white px-5 py-2.5 font-head font-semibold text-sm text-[#111]">
                {c.code}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Quote + Contact */}
      <Contact defaultBrand={brand.name} />
      <Footer />
    </div>
  );
}
